import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Users, Plus, Mail, UserPlus, Calendar, CheckCircle, Clock, Search, MoreVertical, FileText } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { ClientDetail } from "./ClientDetail";

interface ClinicianClientsProps {
  clients: any[];
  sessions: any[];
  canAddClient: boolean;
  clinicianId: string;
  onInviteClient: (email: string, message?: string) => Promise<any>;
  onRefresh: () => void;
}

export function ClinicianClients({
  clients,
  sessions,
  canAddClient,
  clinicianId,
  onInviteClient,
  onRefresh
}: ClinicianClientsProps) {
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const { toast } = useToast();

  const handleInvite = async () => {
    if (!inviteEmail) {
      toast({ title: "Email required", variant: "destructive" });
      return;
    }

    setInviting(true);
    try {
      await onInviteClient(inviteEmail, inviteMessage);
      setIsInviteOpen(false);
      setInviteEmail('');
      setInviteMessage('');
      onRefresh();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setInviting(false);
    }
  };

  const getClientSessions = (clientId: string) => {
    return sessions.filter(s => s.client_id === clientId);
  };

  const getLastSession = (clientId: string) => {
    const clientSessions = getClientSessions(clientId)
      .filter(s => s.status === 'completed')
      .sort((a, b) => new Date(b.session_date).getTime() - new Date(a.session_date).getTime());
    return clientSessions[0];
  };

  const getNextSession = (clientId: string) => {
    const today = new Date();
    const clientSessions = getClientSessions(clientId)
      .filter(s => new Date(s.session_date) >= today && s.status !== 'cancelled')
      .sort((a, b) => new Date(a.session_date).getTime() - new Date(b.session_date).getTime());
    return clientSessions[0];
  };

  const filteredClients = clients.filter(client => {
    const name = client.client_profile?.display_name || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const activeClients = filteredClients.filter(c => c.status === 'active');
  const pendingClients = filteredClients.filter(c => c.status === 'pending');
  const archivedClients = filteredClients.filter(c => c.status !== 'active' && c.status !== 'pending');

  // Show client detail view if a client is selected
  if (selectedClient) {
    return (
      <ClientDetail 
        client={selectedClient}
        sessions={sessions}
        clinicianId={clinicianId}
        onBack={() => setSelectedClient(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search clients..." 
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
          <DialogTrigger asChild>
            <Button 
              className="gap-2"
              disabled={!canAddClient}
            >
              <UserPlus className="h-4 w-4" />
              Invite Client
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite a Client</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Send an invitation to connect with your client on Therappi. They'll receive an email with instructions to set up their account and share their data with you.
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium">Client Email</label>
                <Input 
                  type="email"
                  placeholder="client@example.com"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Personal Message (optional)</label>
                <Textarea 
                  placeholder="Add a personal message to your invitation..."
                  value={inviteMessage}
                  onChange={e => setInviteMessage(e.target.value)}
                  rows={3}
                />
              </div>
              <Button 
                onClick={handleInvite} 
                disabled={inviting || !inviteEmail}
                className="w-full"
              >
                {inviting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {!canAddClient && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-amber-500" />
              <div>
                <p className="font-medium text-foreground">Client Limit Reached</p>
                <p className="text-sm text-muted-foreground">
                  Upgrade your plan to add more clients to your practice.
                </p>
              </div>
              <Button size="sm" className="ml-auto">Upgrade</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Invitations */}
      {pendingClients.length > 0 && (
        <Card className="bg-card/50 border-border/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-amber-500" />
              Pending Invitations ({pendingClients.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingClients.map(client => (
                <div 
                  key={client.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20"
                >
                  <Mail className="h-5 w-5 text-amber-500" />
                  <div className="flex-1">
                    <p className="font-medium text-foreground">
                      {client.client_profile?.display_name || 'Awaiting Response'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Invited {format(new Date(client.invited_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Clients */}
      <Card className="bg-card/50 border-border/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CheckCircle className="h-5 w-5 text-primary" />
            Active Clients ({activeClients.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeClients.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">No active clients yet</p>
              <p className="text-sm text-muted-foreground">Invite your first client to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeClients.map(client => {
                const lastSession = getLastSession(client.client_id);
                const nextSession = getNextSession(client.client_id);
                const totalSessions = getClientSessions(client.client_id).filter(s => s.status === 'completed').length;

                return (
                  <div 
                    key={client.id}
                    className="flex items-center gap-4 p-4 rounded-lg bg-background/50 border border-border/20 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => setSelectedClient(client)}
                  >
                    <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground">
                        {client.client_profile?.display_name || 'Client'}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span>{totalSessions} sessions</span>
                        {client.therappi_connected && (
                          <span className="text-primary">• Therappi connected</span>
                        )}
                        {lastSession && (
                          <span>• Last: {format(new Date(lastSession.session_date), 'MMM d')}</span>
                        )}
                      </div>
                    </div>
                    {nextSession && (
                      <div className="hidden sm:block text-right">
                        <p className="text-sm text-muted-foreground">Next session</p>
                        <p className="text-sm font-medium text-foreground">
                          {format(new Date(nextSession.session_date), 'MMM d')} at {nextSession.session_time?.slice(0, 5)}
                        </p>
                      </div>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={e => e.stopPropagation()}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={e => { e.stopPropagation(); setSelectedClient(client); }}>
                          <FileText className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Calendar className="h-4 w-4 mr-2" />
                          Schedule Session
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
